import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { CardShell, FieldLabel, TagPill } from "./card-handler";

// Same convention as bot-form-req.tsx — overridable in dev/staging.
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://journeybuilder.connect.dalmlabs.com";

interface GenericActionCardProps {
  node: ActionNode;
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

// Fallback used only when /v1/agents returns 4xx/5xx or an empty list —
// keeps the picker usable while convohub is unreachable but is NEVER the
// source of truth for production. Two operational defaults are kept here
// because they're semantic ("First Available" routes to whoever's online),
// not real-person IDs that could leak across tenants.
const FALLBACK_AGENTS: AgentOption[] = [
  { id: "", name: "Select agent" },
  { id: "first_available", name: "First Available Agent" },
  { id: "support_team", name: "Support Team" },
];

interface AgentOption {
  id: string;
  name: string;
}

interface BackendAgent {
  id: string;
  name: string;
  email?: string;
  avatar_url?: string;
  is_online?: boolean;
}

export const GenericActionCard: React.FC<GenericActionCardProps> = ({
  node,
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const [tags, setTags] = useState<string[]>(node.config?.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [agents, setAgents] = useState<AgentOption[]>(FALLBACK_AGENTS);

  // Fetch the tenant's real agents from convohub via the journey_builder
  // proxy. Powers the Assign Agent picker. Only fires when this card is
  // actually rendering an Assign Agent action — no point spamming the
  // endpoint from other action cards that share this component.
  useEffect(() => {
    if (node.type !== "Assign Agent") return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${BACKEND_URL}/v1/agents`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: BackendAgent[] = await res.json();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setAgents([
            { id: "", name: "Select agent" },
            ...data.map((a) => ({ id: a.id, name: a.name })),
          ]);
        }
      } catch (err) {
        console.warn("[AssignAgent] Could not fetch /v1/agents — using fallback list", err);
      }
    })();
    return () => { cancelled = true; };
  }, [node.type]);

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    const next = [...tags, trimmed];
    setTags(next);
    setTagInput("");
    onUpdate({ ...node.config, tags: next });
  };

  const removeTag = (tag: string) => {
    const next = tags.filter((t) => t !== tag);
    setTags(next);
    onUpdate({ ...node.config, tags: next });
  };

  return (
    <CardShell title={node.type} onDelete={onDelete} onReset={onReset} bare={bare}>
        {/* Assign Agent */}
        {node.type === "Assign Agent" && (
          <>
            <FieldLabel>Select Agent</FieldLabel>
            <div className="relative mb-3">
              <select
                value={node.config?.agentId ?? ""}
                onChange={(e) => {
                  const agent = agents.find((option) => option.id === e.target.value);
                  onUpdate({
                    ...node.config,
                    agentId: e.target.value,
                    agentName: agent?.name ?? "",
                  });
                }}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 outline-none focus:ring-1 focus:ring-indigo-300"
              >
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
              <div className="absolute right-2 top-2.5 pointer-events-none"><ChevronDown size={16} /></div>
            </div>

            <FieldLabel>Notes (Optional)</FieldLabel>
            <textarea
              rows={3}
              className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300 resize-none"
              placeholder="This is a first time customer."
              value={node.config?.notes ?? ""}
              onChange={(e) => onUpdate({ ...node.config, notes: e.target.value })}
            />
          </>
        )}

        {/* Add or Remove Tags */}
        {node.type === "Add or Remove Tags" && (
          <>
            <FieldLabel>Choose action</FieldLabel>
            <div className="relative mb-3">
              <select
                value={node.config?.action ?? "Add Tags"}
                onChange={(e) => onUpdate({ ...node.config, action: e.target.value })}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none"
              >
                <option>Add Tags</option>
                <option>Remove Tags</option>
              </select>
              <div className="absolute right-2 top-3 pointer-events-none"><ChevronDown size={16} /></div>
            </div>

            <div className="flex gap-1.5 mb-2">
              <input
                className="flex-1 border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-300"
                placeholder="Add tag…"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
              />
              <button onClick={addTag} className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg transition-colors">
                Add
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <TagPill key={tag} label={tag} onRemove={() => removeTag(tag)} />
              ))}
            </div>
          </>
        )}

        {/* Condition */}
        {node.type === "Condition" && node.config?.variant !== "birthday" && (
          <>
            <FieldLabel>Variable</FieldLabel>
            <input
              className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 text-sm mb-2 outline-none focus:ring-1 focus:ring-indigo-300"
              placeholder="message_body"
              value={node.config?.conditions?.[0]?.variable ?? "message_body"}
              onChange={(e) => onUpdate({
                ...node.config,
                conditions: [{ ...(node.config?.conditions?.[0] ?? {}), variable: e.target.value }],
              })}
            />
            <FieldLabel>Operator</FieldLabel>
            <div className="relative mb-2">
              <select
                value={node.config?.conditions?.[0]?.operator ?? "eq"}
                onChange={(e) => onUpdate({
                  ...node.config,
                  conditions: [{ ...(node.config?.conditions?.[0] ?? {}), operator: e.target.value }],
                })}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none"
              >
                <option value="eq">equals</option>
                <option value="neq">not equals</option>
                <option value="contains">contains</option>
                <option value="starts_with">starts with</option>
                <option value="ends_with">ends with</option>
              </select>
              <div className="absolute right-2 top-3 pointer-events-none"><ChevronDown size={16} /></div>
            </div>
            <FieldLabel>Value</FieldLabel>
            <input
              className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300"
              placeholder="yes"
              value={node.config?.conditions?.[0]?.value ?? ""}
              onChange={(e) => onUpdate({
                ...node.config,
                conditions: [{ ...(node.config?.conditions?.[0] ?? {}), value: e.target.value }],
              })}
            />
          </>
        )}

        {/* Close Conversation */}
        {node.type === "Close Conversation" && (
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Closes the current conversation when triggered.</p>
        )}

        {/* Call API */}
        {node.type === "Call API" && (
          <>
            <FieldLabel>URL</FieldLabel>
            <input
              className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 text-sm mb-2 outline-none focus:ring-1 focus:ring-indigo-300"
              placeholder="https://..."
              value={node.config?.url ?? ""}
              onChange={(e) => onUpdate({ ...node.config, url: e.target.value })}
            />
            <FieldLabel>Method</FieldLabel>
            <div className="relative">
              <select
                value={node.config?.method ?? "POST"}
                onChange={(e) => onUpdate({ ...node.config, method: e.target.value })}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none"
              >
                <option>GET</option>
                <option>POST</option>
                <option>PUT</option>
                <option>DELETE</option>
              </select>
              <div className="absolute right-2 top-3 pointer-events-none"><ChevronDown size={16} /></div>
            </div>
          </>
        )}

        {/* Add to Broadcast */}
        {node.type === "Add to Broadcast" && (
          <>
            <FieldLabel>Template name</FieldLabel>
            <input
              className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 text-sm mb-2 outline-none focus:ring-1 focus:ring-indigo-300"
              placeholder="hello_world"
              value={node.config?.template ?? ""}
              onChange={(e) => onUpdate({ ...node.config, template: e.target.value })}
            />
            <FieldLabel>Language</FieldLabel>
            <div className="relative">
              <select
                value={node.config?.language ?? "en_US"}
                onChange={(e) => onUpdate({ ...node.config, language: e.target.value })}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none"
              >
                <option value="en_US">English (US)</option>
                <option value="en_GB">English (UK)</option>
                <option value="es_ES">Spanish</option>
                <option value="fr_FR">French</option>
                <option value="pt_BR">Portuguese (BR)</option>
              </select>
              <div className="absolute right-2 top-3 pointer-events-none"><ChevronDown size={16} /></div>
            </div>
          </>
        )}
    </CardShell>
  );
};